import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Tooltip } from 'antd';
import {
  MessageOutlined, DoubleRightOutlined, LockOutlined, PushpinOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FilInterne from './FilInterne';
import discussionService from '../../services/discussionService';
import { ContexteDiscussion } from './contexteDiscussion';
import { can, HAB, CHARTE_COLORS as C } from '../../variables/constants';
import { COIN_FLOTTANT, BOTTOM_AU_DESSUS_DU_COIN } from '../BoutonFlottant';

/**
 * Enveloppe d'un écran de dossier : la discussion au coin bas-droite, au-dessus du
 * contenu.
 *
 * CE QUI A CHANGÉ, ET POURQUOI. La discussion était un rail permanent qui POUSSAIT le
 * contenu : une colonne de 46 pixels replié, de 320 à 620 dépliée, prise sur la largeur
 * du dossier. Sur les écrans de dossier - dix-huit onglets, des tableaux à huit colonnes
 * et des formulaires sur deux colonnes - cette largeur manquait, et le contenu se
 * resserrait jusqu'à devenir illisible.
 *
 * Le panneau flotte donc désormais au coin bas-droite, AU-DESSUS du contenu, et ne lui
 * prend plus rien.
 *
 * CE QUE CE CHANGEMENT COÛTE, ET CE QU'IL GARDE. Un panneau flottant recouvre le dossier
 * dont on parle - c'est le défaut que le rail évitait, et il est réel : on discute de ce
 * qu'on regarde. Il est accepté parce que l'espace manquait vraiment, et parce que le
 * panneau se replie d'une touche (C) pour rendre la vue.
 *
 * Le second défaut d'un tiroir, en revanche, n'est PAS accepté : replié, un tiroir ne dit
 * rien, et ce qu'on ignore on ne l'ouvre pas. La bulle repliée porte donc toujours les
 * avatars des participants et le compte de non-lus. « Il y a une conversation ici, trois
 * personnes dedans, deux messages que je n'ai pas lus » est l'information qui donne envie
 * de déplier, et elle survit au déplacement.
 *
 * ELLE SE POSE AU-DESSUS DU BOUTON D'EXPORT, qui occupe le même coin. La géométrie vient
 * de `BoutonFlottant` - `BOTTOM_AU_DESSUS_DU_COIN` - et non de valeurs recopiées : deux
 * positions tenues à la main dérivent, et la collision ne se verrait que sur un écran qui
 * porte les deux.
 *
 * POURQUOI PAS UN ONGLET. Un seizième onglet est mort-né : personne ne va chercher une
 * conversation en position seize, et l'ouvrir ferait perdre le dossier de vue.
 *
 * Le sondage du résumé tourne même panneau replié - c'est ce qui alimente le badge -
 * mais il ne charge AUCUN message : cette charge-là attend qu'on déplie.
 */

const CLE_OUVERT = 'discussion:rail:ouvert';
const CLE_LARGEUR = 'discussion:rail:largeur';

const LARGEUR_MIN = 320;
const LARGEUR_MAX = 620;
const LARGEUR_DEFAUT = 400;

const POLL_RESUME_MS = 60000;

const AVATARS = ['#1a6b3c', '#7c3aed', '#b45309', '#1d4ed8', '#be123c', '#0f766e'];
const couleurAvatar = (cle = '') => {
  let somme = 0;
  for (let i = 0; i < cle.length; i += 1) somme += cle.charCodeAt(i);
  return AVATARS[somme % AVATARS.length];
};

const lireBool = (cle, defaut) => {
  const v = localStorage.getItem(cle);
  return v === null ? defaut : v === '1';
};

export default function DiscussionDossier({ entiteType, entiteId, children }) {
  const { t } = useTranslation('commun');
  const peutLire = can(HAB.DISCUSSION_READ);

  const [ouvert, setOuvert] = useState(() => lireBool(CLE_OUVERT, false));
  const [largeur, setLargeur] = useState(() => {
    const v = parseInt(localStorage.getItem(CLE_LARGEUR), 10);
    return Number.isFinite(v) ? Math.min(Math.max(v, LARGEUR_MIN), LARGEUR_MAX) : LARGEUR_DEFAUT;
  });
  const [resume, setResume] = useState(null);
  const [ancre, setAncre] = useState(null);

  const [params] = useSearchParams();
  const messageCible = params.get('discussion');

  const redimensionnement = useRef(null);

  // ── Persistance ──────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem(CLE_OUVERT, ouvert ? '1' : '0'); }, [ouvert]);
  useEffect(() => { localStorage.setItem(CLE_LARGEUR, String(largeur)); }, [largeur]);

  // Un lien profond - courriel de mention, clic dans la cloche - doit ouvrir le
  // panneau de lui-même : arriver sur un dossier replié après avoir cliqué « Répondre »
  // laisserait l'agent devant l'écran qu'il essayait justement d'atteindre.
  useEffect(() => { if (messageCible) setOuvert(true); }, [messageCible]);

  // ── Résumé, y compris panneau replié ─────────────────────────────────────
  const chargerResume = useCallback(async () => {
    if (!peutLire || !entiteId) return;
    try { setResume(await discussionService.resume(entiteType, entiteId)); }
    catch { /* un échec de sondage n'a pas à interrompre l'agent */ }
  }, [entiteType, entiteId, peutLire]);

  useEffect(() => {
    chargerResume();
    const id = setInterval(chargerResume, POLL_RESUME_MS);
    return () => clearInterval(id);
  }, [chargerResume]);

  // ── Raccourci clavier ────────────────────────────────────────────────────
  // « C » comme commenter. Ignoré dès qu'un champ a le focus, sinon la touche
  // disparaîtrait de tous les formulaires du dossier.
  useEffect(() => {
    if (!peutLire) return undefined;
    const surTouche = (e) => {
      const cible = e.target;
      const saisie = cible?.tagName === 'INPUT' || cible?.tagName === 'TEXTAREA' || cible?.isContentEditable;
      if (saisie || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setOuvert((o) => !o);
      }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [peutLire]);

  // ── Redimensionnement ────────────────────────────────────────────────────
  useEffect(() => {
    const surMouvement = (e) => {
      if (redimensionnement.current == null) return;
      const delta = redimensionnement.current.x - e.clientX;
      const nouvelle = redimensionnement.current.largeur + delta;
      setLargeur(Math.min(Math.max(nouvelle, LARGEUR_MIN), LARGEUR_MAX));
    };
    const surRelache = () => { redimensionnement.current = null; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', surMouvement);
    window.addEventListener('mouseup', surRelache);
    return () => {
      window.removeEventListener('mousemove', surMouvement);
      window.removeEventListener('mouseup', surRelache);
    };
  }, []);

  // ── Contexte d'ancrage ───────────────────────────────────────────────────
  const compteursAncres = useMemo(() => {
    const carte = {};
    (resume?.ancres || []).forEach((a) => { carte[`${a.ancreType}:${a.ancreTrackingId}`] = a.nombre; });
    return carte;
  }, [resume]);

  const valeurContexte = useMemo(() => ({
    disponible: peutLire && Boolean(entiteId),
    compteursAncres,
    ouvrir: () => setOuvert(true),
    ouvrirSur: (nouvelleAncre) => { setAncre(nouvelleAncre); setOuvert(true); },
  }), [peutLire, entiteId, compteursAncres]);

  if (!peutLire) return children;

  const nonLus = resume?.nonLus || 0;
  const participants = resume?.participants || [];

  return (
    <ContexteDiscussion.Provider value={valeurContexte}>
      <>
        {/* Le dossier prend toute la largeur : plus rien ne lui est pris. */}
        {children}

        {/* LA BULLE REPLIEE. Horizontale et non verticale : au coin bas-droite, une
            colonne d'avatars monterait vers le haut de l'ecran sans raison. */}
        {!ouvert && (
          <div
            className="cb-discussion-rail"
            onClick={() => setOuvert(true)}
            title={t('discussionDossier.discussionToucheC')}
            style={{
              position: 'fixed',
              right: COIN_FLOTTANT.right,
              bottom: BOTTOM_AU_DESSUS_DU_COIN,
              zIndex: COIN_FLOTTANT.zIndex,
              display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8,
              padding: '8px 12px', cursor: 'pointer',
              background: '#fff', border: `1px solid ${C.border}`, borderRadius: 24,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.14)',
            }}
          >
            <Tooltip title={t('discussionDossier.discussionToucheC')} placement="left">
              <Badge count={nonLus} size="small" offset={[3, -2]}>
                <MessageOutlined style={{ fontSize: 18, color: nonLus ? C.greenMid : C.muted }} />
              </Badge>
            </Tooltip>

            {/* Les avatars sont le cœur du rail : ils disent qu'une conversation
                existe et qui y participe, sans rien ouvrir. */}
            {participants.slice(0, 4).map((p) => (
              <Tooltip key={p.utilisateurTrackingId} title={`${p.prenoms || ''} ${p.nom || ''}`.trim()} placement="top">
                <span style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: couleurAvatar(p.nom || ''), color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700,
                }}>
                  {p.initiales}
                </span>
              </Tooltip>
            ))}
            {participants.length > 4 && (
              <span style={{ fontSize: 10, color: C.faint, fontWeight: 600 }}>
                +{participants.length - 4}
              </span>
            )}

            {resume?.epingles?.length > 0 && (
              <Tooltip title={t('discussionDossier.messagesEpingles', { nombre: resume.epingles.length })} placement="top">
                {/* Une icone, et non un emoji : le dessin d'un emoji depend du systeme. */}
                <PushpinOutlined style={{ fontSize: 13, color: C.muted }} />
              </Tooltip>
            )}
          </div>
        )}

        {/* LE PANNEAU DEPLIE, au meme coin. `maxWidth` le garde dans la fenetre sur un
            petit ecran : une largeur memorisee a 620 sur un grand ecran deborderait. */}
        {ouvert && (
          <div className="cb-discussion-rail" style={{
            position: 'fixed',
            right: COIN_FLOTTANT.right,
            bottom: BOTTOM_AU_DESSUS_DU_COIN,
            zIndex: COIN_FLOTTANT.zIndex,
            width: largeur,
            maxWidth: `calc(100vw - ${COIN_FLOTTANT.right * 2}px)`,
            display: 'flex', alignItems: 'stretch',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
            borderRadius: 10,
          }}>
            {/* Poignée */}
            <div
              onMouseDown={(e) => {
                redimensionnement.current = { x: e.clientX, largeur };
                document.body.style.userSelect = 'none';
              }}
              title={t('discussionDossier.redimensionner')}
              style={{ width: 6, cursor: 'col-resize', flexShrink: 0 }}
            />

            <div style={{
              flex: 1, minWidth: 0, background: '#fff', border: `1px solid ${C.border}`,
              borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column',
              // La place laissee en bas par le coin flottant, et un peu d'air en haut.
              maxHeight: `calc(100vh - ${BOTTOM_AU_DESSUS_DU_COIN + 24}px)`,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, marginBottom: 10,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: C.text }}>
                  <LockOutlined style={{ color: C.greenMid }} />
                  {t('discussionDossier.discussionInterne')}
                </span>
                <Tooltip title={t('discussionDossier.replierToucheC')}>
                  <DoubleRightOutlined
                    onClick={() => setOuvert(false)}
                    style={{ cursor: 'pointer', color: C.muted, fontSize: 13 }}
                  />
                </Tooltip>
              </div>

              <FilInterne
                entiteType={entiteType}
                entiteId={entiteId}
                hauteur={Math.max(window.innerHeight - 320, 260)}
                ancre={ancre}
                onAncreChange={setAncre}
                messageCible={messageCible}
                onResume={setResume}
              />
            </div>
          </div>
        )}
      </>
    </ContexteDiscussion.Provider>
  );
}
